import { hexLen, hexToWorld, type Hex } from '../hex.ts';
import { imin } from '../math/fixed.ts';
import { deriveSeed, rngInt, seedRng } from '../math/rng.ts';

/**
 * The salt flats (docs/sendas/paladin.md, chapter 2; §4, «Las salinas»): square pools of still water in rows, white
 * dykes of salt between them, the gate house in the middle. One board for the three acts, so the place is learned:
 *
 * - The pools are rectangles in world units (the render draws them as they are): five rows of four, `PITCH_X` and
 *   `PITCH_Y` apart, each `HALF_X` × `HALF_Y` from its middle. A cell belongs to the pool its centre falls in; the rest
 *   is dyke (salt).
 * - The puzzle's six pools are the middle two columns of rows 1 to 3, the three steps: on top the altar's (A) and P;
 *   in the middle M under A and N under P; below B under M and C under N. Its gates: A–P, A→M, P→N, M–N, M→B, N→C (§4,
 *   «Ejemplo verificado»). The altar's column is left or right by the seed; its marks too, checked by every order of
 *   gates (`sluiceSolve`): a short way of three to five gates (one or two of them), none that opens one of the altar's
 *   gates first.
 * - The rest are the outer pools, full (the sea fills them: the drowned climb out of them). In the test four of them,
 *   one on each way to the altar, have dried to a crust of salt: the ways' tracks show there.
 * - The four ways to the altar in the test (north, its outer side, its inner side through P, south), each through its
 *   dry pool far out and over a dyke near the altar (the choke, where salt lasts).
 *
 * Integers only, from the seed: the server builds the same one. x right, y down (the sea's side at the top).
 */

export const SALT_RADIUS = 9;
const R = SALT_RADIUS;
export const SALT_PITCH_X = 4200;
export const SALT_PITCH_Y = 3400;
export const SALT_HALF_X = 1300;
export const SALT_HALF_Y = 1000;
/** Marks a pool holds. */
export const SALT_CAP = 4;
/** Pools a row, rows. */
const COLS = 4;
const ROWS = 5;

export interface SaltPool {
  x: number;
  y: number;
  /** Its column (0..3) and row (0..4). */
  col: number;
  row: number;
}

export interface SaltGate {
  /** From (the upper, or either along a step) and to. */
  a: number;
  b: number;
  /** Down a step (a above b), or along one. */
  down: boolean;
  x: number;
  y: number;
}

export interface SaltWay {
  /** Its points from beyond the fog's rim to the altar (world). */
  pts: { x: number; y: number }[];
  /** The point over a dyke near the altar (salt lasts there). */
  choke: number;
  /** Its dry outer pool. */
  pool: number;
}

export interface SaltLayout {
  pools: SaltPool[];
  /** The puzzle's pools by name (indices into `pools`): A (the altar's), P, M, N, B, C. */
  puzzle: { A: number; P: number; M: number; N: number; B: number; C: number };
  gates: SaltGate[];
  /** Marks at the start of the gates' act (the outer pools full). */
  start: number[];
  /** The gate house (world) and its cells (nothing walks them), its door (where Marit keeps, the people stand). */
  house: { x: number; y: number };
  door: { x: number; y: number };
  rocks: Hex[];
  /** The fog's act: where the hero and Marit come in from, and the way to the door along the dykes. */
  enter: { x: number; y: number };
  route: { x: number; y: number }[];
  /** The test: the four ways, and the outer pools dried to salt (one a way). */
  ways: SaltWay[];
  dry: number[];
  /** The shortest ways through the gates (gate indices), and the side the altar is on (−1 left, +1 right). */
  solutions: number[][];
  side: number;
}

const poolAt = (col: number, row: number): number => row * COLS + col;
const colX = (col: number): number => (2 * col - (COLS - 1)) * (SALT_PITCH_X / 2);
const rowY = (row: number): number => (row - 2) * SALT_PITCH_Y;

/** The pool a world point lies in (−1: a dyke). */
export const saltPoolAt = (pools: readonly SaltPool[], x: number, y: number): number => {
  for (let i = 0; i < pools.length; i++) {
    const p = pools[i] as SaltPool;
    if (x > p.x - SALT_HALF_X && x < p.x + SALT_HALF_X && y > p.y - SALT_HALF_Y && y < p.y + SALT_HALF_Y) return i;
  }
  return -1;
};

/**
 * The marks after a gate's water has run: down a step until the upper is dry or the lower full; along a step level,
 * the odd mark kept by the fuller one (§4). Returns the new pair.
 */
export const sluiceFlow = (down: boolean, a: number, b: number, cap = SALT_CAP): [number, number] => {
  if (down) {
    const m = imin(a, cap - b);
    return [a - m, b + m];
  }
  const t = a + b;
  const lo = t >> 1;
  const hi = t - lo;
  if (hi === lo) return [lo, lo];
  return a >= b ? [hi, lo] : [lo, hi];
};

/**
 * Every order of gates (each turns once, then locks) that drains the altar's pool, shortest first; the ones found up to
 * the shortest length + 1. For the layout's check, the tests and the bot.
 */
export const sluiceSolve = (gates: readonly SaltGate[], start: readonly number[], altar: number, limit = 6): number[][] => {
  const out: number[][] = [];
  let best = 99;
  const walk = (lv: number[], used: number, seq: number[]): void => {
    if ((lv[altar] as number) === 0) {
      if (seq.length < best) best = seq.length;
      out.push(seq.slice());
      return;
    }
    if (seq.length >= limit || seq.length > best) return;
    for (let g = 0; g < gates.length; g++) {
      if (used & (1 << g)) continue;
      const G = gates[g] as SaltGate;
      const [na, nb] = sluiceFlow(G.down, lv[G.a] as number, lv[G.b] as number);
      const next = lv.slice();
      next[G.a] = na;
      next[G.b] = nb;
      seq.push(g);
      walk(next, used | (1 << g), seq);
      seq.pop();
    }
  };
  walk(start.slice(), 0, []);
  return out.filter((s) => s.length === best).concat(out.filter((s) => s.length === best + 1)).sort((a, b) => a.length - b.length);
};

const build = (seed: number): SaltLayout | null => {
  const rng = seedRng(deriveSeed(seed, 0x5a17));
  const pools: SaltPool[] = [];
  for (let row = 0; row < ROWS; row++) for (let col = 0; col < COLS; col++) pools.push({ x: colX(col), y: rowY(row), col, row });
  // The altar's column, left (1) or right (2).
  const side = rngInt(rng, 2) === 0 ? -1 : 1;
  const ac = side < 0 ? 1 : 2;
  const pc = side < 0 ? 2 : 1;
  const A = poolAt(ac, 1);
  const P = poolAt(pc, 1);
  const M = poolAt(ac, 2);
  const N = poolAt(pc, 2);
  const B = poolAt(ac, 3);
  const C = poolAt(pc, 3);
  const mid = (i: number, j: number): { x: number; y: number } => {
    const a = pools[i] as SaltPool;
    const b = pools[j] as SaltPool;
    return { x: (a.x + b.x) >> 1, y: (a.y + b.y) >> 1 };
  };
  const gate = (a: number, b: number, down: boolean): SaltGate => ({ a, b, down, ...mid(a, b) });
  const gates: SaltGate[] = [gate(A, P, false), gate(A, M, true), gate(P, N, true), gate(M, N, false), gate(M, B, true), gate(N, C, true)];
  const start = pools.map(() => SALT_CAP);
  start[A] = SALT_CAP;
  start[P] = 2 + rngInt(rng, 2);
  start[M] = rngInt(rng, 3);
  start[N] = 2 + rngInt(rng, 3);
  start[B] = 3 + rngInt(rng, 2);
  start[C] = 2 + rngInt(rng, 3);
  const sols = sluiceSolve(gates, start, A);
  const shortest = sols.filter((s) => s.length === (sols[0]?.length ?? 0));
  if (!shortest.length || shortest.length > 2) return null;
  const n = (shortest[0] as number[]).length;
  if (n < 3 || n > 5) return null;
  // Never by an altar's gate first (the obvious one is wrong).
  if (sols.some((s) => s[0] === 0 || s[0] === 1)) return null;
  // The gate house where the middle dyke crosses the one under the middle step; its door to the south (the coast).
  const house = { x: 0, y: (rowY(2) + rowY(3)) >> 1 };
  const rocks: Hex[] = [];
  const near: Hex[] = [];
  for (let r = -R - 2; r <= R + 2; r++)
    for (let q = -R - 2; q <= R + 2; q++) {
      const d = hexLen(q, r);
      if (d > R && d <= R + 2) rocks.push({ q, r });
      if (d > R) continue;
      const w = hexToWorld(q, r);
      if (Math.abs(w.x - house.x) <= 600 && Math.abs(w.y - house.y) <= 500) near.push({ q, r });
    }
  rocks.push(...near);
  const door = { x: house.x, y: house.y + 1250 };
  // The fog's act comes in at the far corner from the house, on the dyke under the bottom row.
  // The fog's act comes in at the top of the far side's dyke (by the sea): down it, along the one under the pools, up the
  // middle to the door.
  const lane = -side * SALT_PITCH_X;
  const low = rowY(4) - (SALT_PITCH_Y >> 1);
  const enter = { x: lane, y: rowY(0) };
  const route = [
    { x: lane, y: low },
    { x: 0, y: low },
    { x: 0, y: door.y + 300 },
  ];
  // The test's ways: north, the outer side, the inner side (through P), south; each through its dry outer pool.
  const a = pools[A] as SaltPool;
  const p = pools[P] as SaltPool;
  const north = poolAt(ac, 0);
  const outer = poolAt(side < 0 ? 0 : 3, 1);
  const inner = poolAt(side < 0 ? 3 : 0, 1);
  const south = poolAt(ac, 4);
  const dyY = a.y - (SALT_PITCH_Y >> 1);
  const ways: SaltWay[] = [
    { pts: [{ x: a.x + side * 600, y: -9400 }, { x: (pools[north] as SaltPool).x, y: (pools[north] as SaltPool).y }, { x: a.x, y: dyY }, { x: a.x, y: a.y - 700 }], choke: 2, pool: north },
    { pts: [{ x: side * 10400, y: a.y - 400 }, { x: (pools[outer] as SaltPool).x, y: a.y }, { x: a.x + side * (SALT_PITCH_X >> 1), y: a.y }, { x: a.x + side * 900, y: a.y }], choke: 2, pool: outer },
    { pts: [{ x: -side * 10400, y: a.y + 500 }, { x: (pools[inner] as SaltPool).x, y: a.y }, { x: p.x, y: p.y }, { x: (a.x + p.x) >> 1, y: a.y }, { x: a.x - side * 900, y: a.y }], choke: 3, pool: inner },
    { pts: [{ x: a.x - side * 500, y: 9400 }, { x: (pools[south] as SaltPool).x, y: (pools[south] as SaltPool).y }, { x: a.x, y: (pools[B] as SaltPool).y }, { x: a.x, y: (pools[M] as SaltPool).y }, { x: a.x, y: a.y + (SALT_PITCH_Y >> 1) }, { x: a.x, y: a.y + 700 }], choke: 4, pool: south },
  ];
  const dry = [north, outer, inner, south];
  return { pools, puzzle: { A, P, M, N, B, C }, gates, start, house, door, rocks, enter, route, ways, dry, solutions: sols, side };
};

const CACHE = new Map<number, SaltLayout>();

/** The salt flats for a fight's seed. */
export const saltLayout = (seed: number): SaltLayout => {
  const hit = CACHE.get(seed);
  if (hit) return hit;
  let L: SaltLayout | null = null;
  for (let i = 0; i < 400 && !L; i++) L = build(deriveSeed(seed, i));
  if (!L) throw new Error('no salt layout');
  if (CACHE.size > 64) CACHE.clear();
  CACHE.set(seed, L);
  return L;
};
